/**
 * ContractorSelector Component
 * Search and select a contractor by phone, firm name, code, GST, PAN, etc.
 * Filter by category, PWD class, status.
 * Reusable across admin and contractor views.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ListSkeleton } from '@/components/base/Skeleton';
import { createPortal } from 'react-dom';
import { contractorsApi } from '../services/wmsService';
import { usePwdClasses } from '../hooks/useSetupMasters';
import { WMS_POPUP_HEADER, WMS_POPUP_TITLE, WMS_POPUP_SUBTITLE, WMS_POPUP_ICON, WMS_POPUP_CLOSE } from './common/wmsPopupHeader';

interface ContractorSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (contractor: any) => void;
  title?: string;
  onViewProfile?: (contractor: any) => void;
}

const STATUSES = [
  { cd: 'ACTIVE', nm: 'Active' },
  { cd: 'SUSPENDED', nm: 'Suspended' },
  { cd: 'BLACKLISTED', nm: 'Blacklisted' },
  { cd: 'DEREGISTERED', nm: 'Deregistered' },
];

const fmtCurrency = (n: number) => {
  if (!n) return '-';
  const cr = Number(n) / 10000000;
  return `${cr.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Cr`;
};

export default function ContractorSelector({ isOpen, onClose, onSelect, title = 'Select Contractor', onViewProfile }: ContractorSelectorProps) {
  const [search, setSearch] = useState('');
  const [contractors, setContractors] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [categories, setCategories] = useState<any[]>([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  // From Setup -> PWD Classes. The option VALUE is now the code, not the label: the
  // backend filters c.pwdclass_cd = $1 exactly, so the old label-valued options
  // ('Class I' against a stored 'CLASS_I') silently matched nothing.
  const pwdClasses = usePwdClasses();
  const [selectedPwdClass, setSelectedPwdClass] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedContractor, setSelectedContractor] = useState<any>(null);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const limit = 20;

  // Load categories on mount
  useEffect(() => {
    if (!isOpen) return;
    contractorsApi.getCategories?.().then(setCategories).catch(() => {});
  }, [isOpen]);

  const loadContractors = useCallback(async (pg = 1) => {
    setLoading(true);
    setLoadError(null);
    try {
      const filters: any = {};
      if (search) filters.search = search;
      if (selectedCategory) filters.categoryId = parseInt(selectedCategory);
      if (selectedPwdClass) filters.pwdClass = selectedPwdClass;
      if (selectedStatus) filters.status = selectedStatus;
      const result = await contractorsApi.getAll(filters, pg, limit);
      setContractors(result.data || []);
      setTotal(result.pagination?.total || result.data?.length || 0);
      setPage(pg);
    } catch (err: any) {
      console.error('ContractorSelector load error:', err);
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Failed to load contractors';
      setLoadError(msg);
      setContractors([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [search, selectedCategory, selectedPwdClass, selectedStatus]);

  // Debounced search
  useEffect(() => {
    if (!isOpen) return;
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => loadContractors(1), 300);
    return () => { if (searchTimer.current) clearTimeout(searchTimer.current); };
  }, [search, selectedCategory, selectedPwdClass, selectedStatus, isOpen]);

  const handleSelect = (c: any) => {
    onSelect(c);
    onClose();
  };

  if (!isOpen) return null;

  const totalPages = Math.ceil(total / limit);

  const content = (
    <div className="fixed inset-0 z-[10002] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose}></div>
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-3xl mx-4 max-h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className={`${WMS_POPUP_HEADER} rounded-t-xl`}>
          <div className="flex items-center gap-2 min-w-0">
            <div className={WMS_POPUP_ICON}>
              <i className="ri-user-search-line" />
            </div>
            <div className="min-w-0">
              <h3 className={WMS_POPUP_TITLE}>{title}</h3>
              <p className={WMS_POPUP_SUBTITLE}>{total} contractor{total !== 1 ? 's' : ''} found</p>
            </div>
          </div>
          <button onClick={onClose} className={WMS_POPUP_CLOSE}>
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Search + Filters */}
        <div className="px-5 py-3 border-b bg-gray-50 space-y-2">
          {/* Search bar */}
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, phone, firm, code, GST, PAN..."
              className="w-full pl-9 pr-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-300 focus:border-teal-400"
              autoFocus
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <i className="ri-close-circle-fill"></i>
              </button>
            )}
          </div>
          {/* Filter row */}
          <div className="flex items-center gap-2 flex-wrap">
            <select value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)}
              className="px-2.5 py-1.5 border rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-teal-300 bg-white">
              <option value="">All Categories</option>
              {categories.map((c: any) => <option key={c.cntrctr_ctgry_id} value={c.cntrctr_ctgry_id}>{c.cntrctr_ctgry_nm}</option>)}
            </select>
            <select value={selectedPwdClass} onChange={e => setSelectedPwdClass(e.target.value)}
              className="px-2.5 py-1.5 border rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-teal-300 bg-white">
              <option value="">All Classes</option>
              {pwdClasses.map(c => <option key={c.pwd_cls_id} value={c.pwd_cls_cd}>{c.pwd_cls_nm}</option>)}
            </select>
            <select value={selectedStatus} onChange={e => setSelectedStatus(e.target.value)}
              className="px-2.5 py-1.5 border rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-teal-300 bg-white">
              <option value="">All Statuses</option>
              {STATUSES.map(s => <option key={s.cd} value={s.cd}>{s.nm}</option>)}
            </select>
            {(selectedCategory || selectedPwdClass || selectedStatus) && (
              <button onClick={() => { setSelectedCategory(''); setSelectedPwdClass(''); setSelectedStatus(''); }}
                className="px-2 py-1.5 text-[10px] text-gray-500 hover:text-gray-700 flex items-center gap-0.5">
                <i className="ri-filter-off-line"></i> Clear
              </button>
            )}
          </div>
        </div>

        {/* Contractor List */}
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="p-4"><ListSkeleton rows={5} /></div>
          ) : loadError ? (
            <div className="flex flex-col items-center justify-center h-48 text-center px-6">
              <i className="ri-error-warning-line text-3xl text-red-500 mb-2"></i>
              <p className="text-sm font-medium text-red-700">Couldn't load contractors</p>
              <p className="text-xs text-gray-500 mt-1 max-w-md">{loadError}</p>
              <button
                onClick={() => loadContractors(1)}
                className="mt-3 px-3 py-1.5 text-xs font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-md flex items-center gap-1"
              >
                <i className="ri-refresh-line"></i> Retry
              </button>
            </div>
          ) : contractors.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-gray-400">
              <i className="ri-user-unfollow-line text-3xl mb-2"></i>
              <p className="text-sm">
                {search || selectedCategory || selectedPwdClass || selectedStatus
                  ? 'No contractors match your filters'
                  : 'No contractors available in the master list'}
              </p>
              {(search || selectedCategory || selectedPwdClass || selectedStatus) && (
                <button
                  onClick={() => {
                    setSearch('');
                    setSelectedCategory('');
                    setSelectedPwdClass('');
                    setSelectedStatus('');
                  }}
                  className="mt-2 px-2.5 py-1 text-[11px] text-teal-700 bg-teal-50 border border-teal-200 rounded-md hover:bg-teal-100"
                >
                  <i className="ri-filter-off-line mr-1"></i>Clear filters
                </button>
              )}
            </div>
          ) : (
            <div className="divide-y">
              {contractors.map((c: any) => {
                const isSelected = selectedContractor?.cntrctr_id === c.cntrctr_id;
                const isBlacklisted = c.cntrctr_sts_cd === 'BLACKLISTED' || c.blklst_in;
                return (
                  <div key={c.cntrctr_id}
                    onClick={() => setSelectedContractor(isSelected ? null : c)}
                    className={`px-5 py-3 cursor-pointer transition-colors ${
                      isSelected ? 'bg-teal-50 border-l-4 border-l-teal-500' :
                      isBlacklisted ? 'bg-red-50/30 hover:bg-red-50/50' :
                      'hover:bg-gray-50'
                    }`}>
                    <div className="flex items-start gap-3">
                      {/* Avatar */}
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        isBlacklisted ? 'bg-red-100 text-red-600' : 'bg-teal-100 text-teal-700'
                      }`}>
                        <i className="ri-user-3-line text-lg"></i>
                      </div>
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm text-gray-900">{c.cntrctr_nm}</span>
                          <span className="font-mono text-[10px] text-gray-400">{c.cntrctr_cd}</span>
                          <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${
                            c.cntrctr_sts_cd === 'ACTIVE' ? 'bg-green-100 text-green-700' :
                            c.cntrctr_sts_cd === 'SUSPENDED' ? 'bg-amber-100 text-amber-700' :
                            c.cntrctr_sts_cd === 'BLACKLISTED' ? 'bg-red-100 text-red-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>{c.cntrctr_sts_nm || c.cntrctr_sts_cd}</span>
                          {c.cntrctr_ctgry_nm && (
                            <span className="px-1.5 py-0.5 rounded text-[9px] bg-blue-50 text-blue-600">{c.cntrctr_ctgry_nm}</span>
                          )}
                          {c.pwdclass_cd && (
                            <span className="px-1.5 py-0.5 rounded text-[9px] bg-violet-50 text-violet-600">{c.pwdclass_cd}</span>
                          )}
                          {c.msme_rgstrd_in && (
                            <span className="px-1.5 py-0.5 rounded text-[9px] bg-emerald-50 text-emerald-600">MSME</span>
                          )}
                        </div>
                        {/* Firm name */}
                        {c.frm_nm && c.frm_nm !== c.cntrctr_nm && (
                          <p className="text-[10px] text-gray-500 mt-0.5"><i className="ri-building-line mr-0.5"></i>{c.frm_nm}</p>
                        )}
                        {/* Contact info row */}
                        <div className="flex items-center gap-3 mt-1 text-[10px] text-gray-500 flex-wrap">
                          {c.phn_nu && <span><i className="ri-phone-line mr-0.5"></i>{c.phn_nu}</span>}
                          {c.eml_tx && <span><i className="ri-mail-line mr-0.5"></i>{c.eml_tx}</span>}
                          {c.cty_nm && <span><i className="ri-map-pin-line mr-0.5"></i>{c.cty_nm}{c.stte_nm ? `, ${c.stte_nm}` : ''}</span>}
                        </div>
                        {/* Stats row */}
                        <div className="flex items-center gap-3 mt-1 text-[10px] text-gray-500">
                          {c.active_contracts > 0 && <span className="text-teal-600">{c.active_contracts} contract{c.active_contracts > 1 ? 's' : ''}</span>}
                          {c.total_contract_value > 0 && <span className="text-emerald-600">₹{fmtCurrency(Number(c.total_contract_value))}</span>}
                          {c.active_works_ct > 0 && <span className="text-blue-600">{c.active_works_ct} active work{c.active_works_ct > 1 ? 's' : ''}</span>}
                          {c.completed_works_ct > 0 && <span className="text-green-600">{c.completed_works_ct} completed</span>}
                        </div>
                      </div>
                      {/* Select indicator */}
                      <div className="flex-shrink-0 mt-1">
                        {isSelected ? (
                          <div className="w-6 h-6 rounded-full bg-teal-500 text-white flex items-center justify-center">
                            <i className="ri-check-line text-sm"></i>
                          </div>
                        ) : (
                          <div className="w-6 h-6 rounded-full border-2 border-gray-200"></div>
                        )}
                      </div>
                    </div>

                    {/* Expanded details when selected */}
                    {isSelected && (
                      <div className="mt-3 pt-3 border-t border-teal-200">
                        <div className="grid grid-cols-4 gap-3 text-xs">
                          {c.rgstrn_nu && <div><span className="text-gray-400 block">Reg. No</span><span className="text-gray-700">{c.rgstrn_nu}</span></div>}
                          {c.gstn_nu && <div><span className="text-gray-400 block">GSTIN</span><span className="text-gray-700 font-mono text-[10px]">{c.gstn_nu}</span></div>}
                          {c.pan_nu && <div><span className="text-gray-400 block">PAN</span><span className="text-gray-700 font-mono text-[10px]">{c.pan_nu}</span></div>}
                          {c.max_tndr_cpcty_at > 0 && <div><span className="text-gray-400 block">Max Tender Capacity</span><span className="text-gray-700">₹{fmtCurrency(Number(c.max_tndr_cpcty_at))}</span></div>}
                          {c.avg_trnvr_at > 0 && <div><span className="text-gray-400 block">Avg Turnover</span><span className="text-gray-700">₹{fmtCurrency(Number(c.avg_trnvr_at))}</span></div>}
                          {c.cntct_prsn_nm && <div><span className="text-gray-400 block">Contact Person</span><span className="text-gray-700">{c.cntct_prsn_nm}</span></div>}
                          {c.addr_tx && <div className="col-span-2"><span className="text-gray-400 block">Address</span><span className="text-gray-700">{c.addr_tx}</span></div>}
                        </div>
                        {onViewProfile && (
                          <button onClick={(e) => { e.stopPropagation(); onViewProfile(c); }}
                            className="mt-2 px-2.5 py-1 text-[10px] text-teal-700 bg-teal-50 border border-teal-200 rounded-lg hover:bg-teal-100 flex items-center gap-1">
                            <i className="ri-external-link-line"></i> Open Full Profile
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t bg-gray-50 flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs text-gray-500">
            {total > limit && (
              <>
                <button onClick={() => loadContractors(page - 1)} disabled={page <= 1}
                  className="px-2 py-1 border rounded hover:bg-gray-100 disabled:opacity-30"><i className="ri-arrow-left-s-line"></i></button>
                <span>Page {page} of {totalPages}</span>
                <button onClick={() => loadContractors(page + 1)} disabled={page >= totalPages}
                  className="px-2 py-1 border rounded hover:bg-gray-100 disabled:opacity-30"><i className="ri-arrow-right-s-line"></i></button>
              </>
            )}
            <span>{total} contractor{total !== 1 ? 's' : ''}</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-1.5 text-sm text-gray-600 hover:text-gray-800 rounded-lg hover:bg-gray-100">Cancel</button>
            <button onClick={() => selectedContractor && handleSelect(selectedContractor)}
              disabled={!selectedContractor}
              className="px-4 py-1.5 text-sm text-white bg-teal-600 hover:bg-teal-700 rounded-lg disabled:opacity-50 font-medium flex items-center gap-1.5">
              <i className="ri-check-line"></i> Select Contractor
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
